import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'How Many Donors Do You Need to Reach Full Support? A Simple Math-First Framework';
const DESCRIPTION =
    "Most missionaries size their support goal in dollars and stall on donor count. A practical framework for working backward from your monthly number to how many supporters you actually need — and at what gift size.";

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover11.jpg',
            datePublished: '2026-08-05',
            dateModified: '2026-08-05',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/how-many-donors-do-you-need-for-full-support',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'How many donors does a missionary typically need?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'It depends entirely on your target monthly number and average gift size, not on a fixed rule. A missionary needing $4,000/month with an average recurring gift of $50 needs roughly 80 donors; the same goal at a $100 average needs 40. Working backward from your actual number beats guessing at a headcount.',
                    },
                },
                {
                    '@type': 'Question',
                    name: 'What is a realistic average monthly gift size?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "For individual donors, $25-$75/month is a realistic working average for most missionary support bases, with a small number of larger gifts and churches pulling the blended average higher. Use your own early pledges as data once you have 10-15 committed, rather than guessing before you've asked anyone.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Should I count churches the same as individual donors?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "No — model them separately. A sending or partner church commitment is often the size of 10-30 individual donors in one line, so a realistic plan usually mixes a handful of church commitments with a broader base of individual monthly donors, rather than assuming either one alone will close the gap.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Why does donor count matter more than just hitting the dollar goal?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'A support base concentrated in a few large donors is fragile — losing one gift can create a real budget gap. A support base spread across more donors at smaller gifts is more resilient: any single donor lapsing is a dip, not a crisis. Donor count is a resilience metric, not just a fundraising milestone.',
                    },
                },
            ],
        },
    ],
};

export default function HowManyDonorsDoYouNeedForFullSupport() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/how-many-donors-do-you-need-for-full-support' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-donor-count-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover11.jpg" alt="A missionary reviewing a support-raising donor list and budget on paper" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>Ask a missionary how much monthly support they need and most can answer immediately. Ask how many donors that will take and most guess — or haven't thought about it at all. That's backwards. Your dollar goal tells you the destination; your donor count tells you how many conversations, letters, and follow-ups actually stand between you and it. Working it out ahead of time turns "I need to raise a lot of support" into a concrete, trackable number of asks.</p>

                    <h2>The basic formula</h2>

                    <p>Donor count is just your monthly goal divided by your average monthly gift:</p>

                    <p><strong>Number of donors needed = Monthly support goal ÷ Average monthly gift</strong></p>

                    <p>If you need $4,000/month and expect an average gift of $50/month, that's 80 donors. At a $100 average, it's 40. At a $25 average — common for younger supporters or students — it's 160. The same dollar goal produces wildly different donor counts depending on what you assume about gift size, which is exactly why this number is worth calculating deliberately rather than leaving implicit. See our <a href="/resources/how-much-support-does-a-missionary-need">guide to building your monthly support budget</a> if you haven't landed on the dollar goal yet, or run your own numbers on the <a href="/calculator">support calculator</a>.</p>

                    <h2>Picking a realistic average gift size</h2>

                    <p>Before you've asked anyone, this number is a guess — and it's worth guessing conservatively rather than optimistically, since an inflated average gift assumption produces a donor-count target you'll blow past without realizing it.</p>

                    <ul>
                        <li><strong>$25-$75/month is a reasonable working average</strong> for individual donors across most missionary support bases — friends, extended family, and church acquaintances giving what fits their own budget.</li>
                        <li><strong>Once you have 10-15 committed donors, replace the guess with real data.</strong> Average their actual pledges. Your real base almost always skews slightly differently than assumed — sometimes lower because more people give small amounts than expected, sometimes higher because a few people surprise you.</li>
                        <li><strong>Recalculate your remaining donor count each time you update the average.</strong> If your first 15 donors average $40 instead of your planned $50, your remaining-donor math needs to shift too, or you'll fall short even while hitting your original headcount target.</li>
                    </ul>

                    <h2>Model churches and major donors separately</h2>

                    <p>Don't fold a sending church's monthly commitment into the same per-donor average as an individual $30 gift — it distorts the number in both directions depending on which one dominates your list. A realistic plan usually has two layers:</p>

                    <ol>
                        <li><strong>A handful of larger, concentrated commitments</strong> — a sending church, a partner church, one or two major individual donors — each worth what 10-30 individual donors would be in one line item.</li>
                        <li><strong>A broader base of individual monthly donors</strong> at a smaller average gift, which is where most of your donor-count target actually lives.</li>
                    </ol>

                    <p>Calculate the two layers separately, then combine them: subtract your expected church/major-donor total from your monthly goal first, and only run the donor-count formula on what's left. Skipping this step is the most common reason a "how many donors do I need" estimate ends up far too high — or dangerously too low if the anchor commitment never materializes.</p>

                    <h2>Why donor count is a resilience metric, not just a milestone</h2>

                    <p>Two missionaries can hit the identical $4,000/month goal in very different ways. One reaches it through 12 donors averaging over $300/month. The other reaches it through 90 donors averaging $44/month. Both are "fully funded" on paper, but the first is one lapsed gift away from a serious budget gap, while the second barely notices any single donor stopping. See our guide on <a href="/resources/diversify-missionary-support-sources">diversifying missionary support sources</a> for a concentration test that makes this concrete — as a rule of thumb, no single donor or church should represent more than 10-15% of your total support. Planning your donor count up front, rather than backing into whatever mix happens to occur, is how you build that resilience by design instead of by luck.</p>

                    <h2>Turning the number into a weekly target</h2>

                    <p>Once you know your donor-count target, break it into a pace you can actually sustain:</p>

                    <ul>
                        <li><strong>List your realistic contact pool first.</strong> Most people can name 100-200 people they could plausibly ask — friends, family, church community, coworkers, alumni networks. Compare that pool to your donor-count target; if your target is close to or above your pool size, you likely need to widen your network (see our guide on <a href="/resources/raising-support-with-no-network">raising support without a big network</a>) rather than push harder on the same list.</li>
                        <li><strong>Expect a response rate, not a 100% conversion.</strong> A typical response rate for a well-written, personal ask lands somewhere around 30-50% of people contacted. If you need 80 donors and expect a 40% conversion, you need to meaningfully engage roughly 200 people over the course of your campaign — not just send 80 letters and wait.</li>
                        <li><strong>Set a weekly ask pace against your timeline.</strong> If you have 90 days and need to engage 200 people, that's roughly 15-16 meaningful contacts a week — a concrete, trackable number, instead of an open-ended "keep asking people" task that's easy to let slide. Our <a href="/resources/raise-support-fast-30-60-90-day-plan">30/60/90-day plan</a> walks through pacing a campaign like this in more detail.</li>
                    </ul>

                    <h2>Frequently asked questions</h2>

                    <p><strong>How many donors does a missionary typically need?</strong><br />
                    It depends on your monthly goal and average gift size. A $4,000/month goal needs about 80 donors at a $50 average, or 40 at a $100 average — calculate it for your own numbers rather than assuming a fixed headcount.</p>

                    <p><strong>What is a realistic average monthly gift size?</strong><br />
                    $25-$75/month is a reasonable starting assumption for individual donors. Replace it with your own average once you have 10-15 committed donors.</p>

                    <p><strong>Should I count churches the same as individual donors?</strong><br />
                    No. Model church and major-donor commitments separately from your individual-donor base, since one church line can equal 10-30 individual gifts.</p>

                    <p><strong>Why does donor count matter more than just hitting the dollar goal?</strong><br />
                    A goal reached through many smaller gifts is more resilient than the same goal reached through a few large ones — losing one donor is a dip instead of a crisis.</p>

                    <h2>Next reads on Devotion Fund</h2>
                    <ul>
                        <li><a href="/resources/how-much-support-does-a-missionary-need">How Much Support Does a Missionary Need? A Realistic Monthly Budget Breakdown</a></li>
                        <li><a href="/resources/diversify-missionary-support-sources">How to Diversify Missionary Support So One Donor or Church Can't Sink Your Budget</a></li>
                        <li><a href="/resources/raise-support-fast-30-60-90-day-plan">How to Raise Support Fast: A 30/60/90-Day Plan for New Missionaries</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>. We wrote this after seeing how many missionaries can state their dollar goal instantly but freeze up when asked how many people that actually requires — turning a vague goal into a concrete donor-count target is one of the fastest ways to make a support campaign feel achievable instead of overwhelming.</p>
                    <p><small><em>Last updated: 2026-08-05.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/how-many-donors-do-you-need-for-full-support" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
